import toGenericErrors from "@casino/legacy/client/components/Auth/Shared/utils/toGenericErrors";
import useRouter from "@casino/legacy/client/hooks/router/useRouter";
import usePhoneNumberHint from "@casino/legacy/client/hooks/usePhoneNumberHint";
import type { ErrorsFragment } from "@workspace/casino_core/mutations/UpdateUserSettingsMutation";
import { setCookie } from "@workspace/cookies/browser";
import type { FieldValues, UseFormSetError } from "react-hook-form";
import useWalletErrors from "./useCheckWalletErrors";

type Args<T extends FieldValues> = {
  setError?: UseFormSetError<T>;
  setErrors?: (errorsFragment: GenericErrorType[]) => void;
  setOtpError?: () => void;
  redirectUrl?: string;
  errors: Array<ErrorsFragment | undefined> | undefined;
  address: string;
};

const useCheckWalletAuthErrors = (authMethod?: WalletAuthMethod) => {
  const { push } = useRouter();
  const { hasSMS2FAErrors, hasOtpErrors, hasAgeLimitErrors } =
    useWalletErrors();
  const { setPhoneNumberHint } = usePhoneNumberHint();

  const walletAuthMethod = authMethod || "metamask";

  const handleAuthErrors = <T extends FieldValues>({
    errors,
    setError,
    setErrors,
    setOtpError,
    redirectUrl,
    address,
  }: Args<T>) => {
    const redirect = redirectUrl?.length ? `&redirect=${redirectUrl}` : "";

    if (!errors) {
      return null;
    }

    const genericErrors = toGenericErrors(errors);
    setErrors?.(genericErrors);
    setPhoneNumberHint(genericErrors);

    const error = errors[0];
    if (!error) {
      return null;
    }
    const isSMS2FAError = hasSMS2FAErrors(error);
    const isOtpError = hasOtpErrors(error);
    const { error: isDateOfBirthError, message: dateOfBirthErrorMessage } =
      hasAgeLimitErrors(error);
    if (isSMS2FAError) {
      setCookie("loginError", String(error.message));
      return push(`/login?redirect=${redirectUrl}`, undefined);
    }
    if (isOtpError) {
      setCookie("loginError", String(error.message));
      setOtpError?.();
      return push(
        `/two-factor-authentication?${walletAuthMethod}=true&addr=${address}${redirect}`,
      );
    }
    if (error.params?.[0]?.value?.includes("password")) {
      return push(
        `/complete-registration?${walletAuthMethod}=true&addr=${address}${redirect}`,
      );
    }

    if (isDateOfBirthError) {
      setError?.("dateOfBirth.day" as never, {
        message: dateOfBirthErrorMessage,
      });
      return push(
        `/complete-registration?${walletAuthMethod}=true&addr=${address}${redirect}`,
      );
    }
    return null;
  };

  return { handleAuthErrors };
};

export default useCheckWalletAuthErrors;
